import { Link } from "react-router-dom";

/**
 * White panel used by the job workspace cards.
 * @param {{ children: React.ReactNode, className?: string }} props
 * @returns {JSX.Element}
 */
export function WorkspaceCard({ children, className = "" }) {
  return (
    <section
      className={`overflow-hidden rounded-[10px] border border-[#e2e7ea] bg-white ${className}`}
    >
      {children}
    </section>
  );
}

/**
 * Label / value pair from the job summary and dispatch cards.
 * @param {{ label: string, value: string, detail?: string, to?: string }} props
 * @returns {JSX.Element}
 */
export function DetailField({ label, value, detail, to }) {
  const valueClass = to
    ? "text-[13px] font-medium leading-[1.4] text-[#167366] hover:underline"
    : "text-[13px] font-medium leading-[1.4] text-[#202b33]";
  return (
    <div className="flex min-w-[160px] flex-1 basis-0 flex-col gap-[7px]">
      <p className="text-[11px] font-normal leading-normal text-[#6e7b84]">{label}</p>
      {to ? (
        <Link to={to} className={valueClass}>
          {value}
        </Link>
      ) : (
        <p className={valueClass}>{value}</p>
      )}
      {detail ? (
        <p className="text-[11px] font-normal leading-normal text-[#6e7b84]">{detail}</p>
      ) : null}
    </div>
  );
}

/**
 * A recorded amount row in the financial summary.
 * @param {{ label: string, value: string }} props
 * @returns {JSX.Element}
 */
export function AmountRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 text-[12px] leading-normal">
      <p className="font-normal text-[#6e7b84]">{label}</p>
      <p className="font-medium text-[#202b33]">{value}</p>
    </div>
  );
}

/**
 * Workspace action button. Shared Button cannot match this size and color.
 * @param {{
 *   children?: React.ReactNode,
 *   icon?: React.ReactNode,
 *   onClick?: () => void,
 *   disabled?: boolean,
 *   tone?: "default" | "primary",
 *   className?: string,
 *   label?: string,
 *   type?: "button" | "submit",
 *   form?: string,
 * }} props
 * @returns {JSX.Element}
 */
export function WorkspaceButton({
  children,
  icon,
  onClick,
  disabled = false,
  tone = "default",
  className = "",
  label,
  type = "button",
  form,
}) {
  const toneClass =
    tone === "primary"
      ? "border-[#167366] bg-[#167366] text-white"
      : "border-[#e2e7ea] bg-white text-[#202b33]";

  return (
    <button
      type={type}
      form={form}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-9 items-center justify-center gap-[7px] rounded-[6px] border px-3 text-[12px] font-semibold leading-normal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#167366] disabled:cursor-not-allowed disabled:opacity-50 ${toneClass} ${className}`}
    >
      {icon}
      {children}
    </button>
  );
}

/**
 * Status pill used beside the job number and in the approval card.
 * @param {{ label: string, tone?: "progress" | "priority" | "approved" | "neutral", dot?: boolean }} props
 * @returns {JSX.Element}
 */
export function StatusPill({ label, tone = "neutral", dot = false }) {
  const tones = {
    progress: "bg-[#edf3f8] text-[#3b698c]",
    priority: "bg-[#fbf3e5] text-[#93651e]",
    approved: "bg-[#eaf4f1] text-[#167366]",
    neutral: "bg-[#f7f9fa] text-[#6e7b84]",
  };
  const dots = {
    progress: "bg-[#3b698c]",
    priority: "bg-[#93651e]",
    approved: "bg-[#167366]",
    neutral: "bg-[#6e7b84]",
  };

  return (
    <span
      className={`inline-flex items-center gap-[6px] rounded-[5px] px-2 py-[5px] text-[11px] font-semibold leading-normal ${tones[tone] || tones.neutral}`}
    >
      {dot ? (
        <span className={`size-[5px] rounded-full ${dots[tone] || dots.neutral}`} />
      ) : null}
      {label}
    </span>
  );
}
